import * as React from "react";
import { Link, LinkProps, useLocation } from "react-router-dom";

import { PageEnvelope } from "@skv/core";
import { OpportunitySearchQueryState } from "@skv/state";

import classNames from "classnames";
import { ROUTE_FAVOURITES } from "~/constants";
import { useUserState } from "~/state";
import { SuggestedOpportunity } from "~/ui/SuggestedOpportunity";

interface ActivityCardProps {
  hover?: boolean;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  hover,
  children,
}: {
  hover?: boolean;
  children: React.ReactNode;
}): JSX.Element => {
  const { isAuthenticated } = useUserState();
  return (
    <div className="nsw-card" css={hover == false && `pointer-events: none;`}>
      <div
        className={classNames("nsw-card__content", {
          "p-4": !isAuthenticated,
        })}
      >
        {children}
      </div>
      {isAuthenticated && (
        <span
          tabIndex={-1}
          aria-hidden="true"
          className="material-icons nsw-material-icons"
        >
          east
        </span>
      )}
    </div>
  );
};

interface ActivityHeaderProps {
  icon?: "favorite" | "email" | "star" | "edit";
  to?: LinkProps["to"];
}

const ActivityHeader: React.FC<ActivityHeaderProps> = ({
  children,
  icon,
  to,
}) => {
  return (
    <>
      <div className="nsw-card__title d-flex align-items-center">
        {icon && (
          <span className="material-icons mr-2" aria-hidden="true">
            {icon}
          </span>
        )}
        {to ? (
          <Link to={to}>
            <div className="d-flex">{children}</div>
          </Link>
        ) : (
          <div
            className="d-flex"
            css={`
              cursor: default;
            `}
          >
            {children}
          </div>
        )}
      </div>
    </>
  );
};

const FavouritesFallback: React.FC = () => {
  return (
    <p className="nsw-card__copy">
      Save your favourite opportunities and organisations using the hearts so
      you can easily find them again.
    </p>
  );
};

interface ActivityDashboardViewModel {
  suggestionCount: number;
  suggestions: Suggestion[];
  emailAlerts: EmailAlert[];
  favourites: Favourite[];
  experiences: any[];
  applications: ActivityEntity[];
  emailAlertCount: number;
  favouriteCount: number;
  experienceCount: number;
  applicationCount: number;
}

interface ActivityEntity {
  id: number;
  name: string;
  url: string;
}

interface Favourite extends ActivityEntity {
  type: string;
}
interface Suggestion extends ActivityEntity {
  organisationName: string;
}

interface EmailAlert extends ActivityEntity {
  searchCriteria: OpportunitySearchQueryState;
}

interface State {
  data: null | ActivityDashboardViewModel;
  status: "loading" | "loaded" | "error";
  error: Error | null;
}

interface ActivityListProps<T = ActivityEntity> {
  data: T[];
  renderData?: (data: T) => React.ReactNode;
}

const ActivityList = <T extends ActivityEntity>({
  data,
  renderData,
}: ActivityListProps<T>): JSX.Element => {
  return (
    <>
      <div className="nsw-card__copy">
        {data.slice(0, 2).map((n) =>
          renderData ? (
            <div className="mb-0" key={n.id}>
              {renderData(n)}
            </div>
          ) : (
            <p
              css={`
                &:last-child {
                  margin-bottom: 0;
                }
              `}
              key={n.id}
            >
              {n.name}
            </p>
          )
        )}
      </div>
    </>
  );
};

export const ActivityDashboard: React.FC = () => {
  const location = useLocation();

  const [state, setState] = React.useState<State>(() => ({
    data: null,
    status: "loading",
    error: null,
  }));

  const { isAuthenticated } = useUserState();

  const pluralize = (count: number, singular: string, plural: string) => {
    return `${count} ${count !== 1 ? plural : singular}`;
  };

  React.useEffect(() => {
    fetch("/api/dashboard", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    })
      .then<PageEnvelope<ActivityDashboardViewModel>>((res) => {
        if (res.ok) {
          return res.json();
        }

        throw Error("");
      })
      .then(({ success, data, errorCode }) => {
        if (success) {
          setState((prev) => ({
            ...prev,
            data,
            status: "loaded",
          }));
          return;
        }
      })
      .catch((err) => {
        //
      });
  }, [isAuthenticated]);

  if (state.status === "loading") {
    return <p className="mt-3">Please wait...</p>;
  }

  if (!isAuthenticated) {
    return (
      <section className="nsw-grid mb-5">
        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard>
            <ActivityHeader icon="favorite" to={ROUTE_FAVOURITES}>
              Favourites
            </ActivityHeader>
            <p className="nsw-card__copy">
              Save your favourite opportunities and organisations using the
              hearts so you can easily find them again.
            </p>
          </ActivityCard>
        </div>
        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard hover={false}>
            <ActivityHeader icon="email">Email alerts</ActivityHeader>
            <p className="nsw-card__copy">
              Email alerts make sure you never miss out on a new opportunity.
            </p>
          </ActivityCard>
        </div>
        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard hover={false}>
            <ActivityHeader icon="star">Experiences</ActivityHeader>
            <p className="nsw-card__copy">
              Keep a record of your volunteering experience.You can include any
              volunteering experience, not just opportunities from this site.
            </p>
          </ActivityCard>
        </div>
        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard hover={false}>
            <ActivityHeader icon="edit">Applications</ActivityHeader>
            <p className="nsw-card__copy">
              This is a list of opportunities you have previously applied for on
              this site, other partner sites, that support the national
              volunteering database.
            </p>
          </ActivityCard>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="nsw-grid mt-lg-4 mb-5">
        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard>
            <ActivityHeader
              icon="favorite"
              to={(loc) => ({
                pathname: "/my-activity/favourites",
                state: loc.state,
              })}
            >
              <span>
                {pluralize(
                  state.data.favouriteCount,
                  "Favourite",
                  "Favourites"
                )}
              </span>
            </ActivityHeader>
            {state.data.favouriteCount > 0 ? (
              <ActivityList data={state.data.favourites} />
            ) : (
              <FavouritesFallback />
            )}
          </ActivityCard>
        </div>

        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard>
            <ActivityHeader
              icon="email"
              to={(loc) => ({
                pathname: "/my-activity/subscriptions",
                state: loc.state,
              })}
            >
              <span>
                {pluralize(
                  state.data.emailAlertCount,
                  "Email alert",
                  "Email alerts"
                )}
              </span>
            </ActivityHeader>
            {state.data.emailAlertCount > 0 ? (
              <ActivityList
                data={state.data.emailAlerts}
                renderData={(n) => <p key={n.id}>{n.name}</p>}
              />
            ) : (
              <p className="nsw-card__copy">
                Save your search at the top or bottom of the search results and
                receive every new opportunity.
              </p>
            )}

            <span
              tabIndex={-1}
              aria-hidden="true"
              className="material-icons nsw-material-icons"
            >
              east
            </span>
          </ActivityCard>
        </div>

        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard>
            <ActivityHeader
              icon="star"
              to={(loc) => ({
                pathname: "/my-activity/experiences",
                state: loc.state,
              })}
            >
              <span>
                {pluralize(
                  state.data.experienceCount,
                  "Experience",
                  "Experiences"
                )}
              </span>
            </ActivityHeader>
            {state.data.experienceCount > 0 ? (
              <ActivityList data={state.data.experiences} />
            ) : (
              <p className="nsw-card__copy">
                Add your volunteer experience to keep a of record of your
                activity.
              </p>
            )}
          </ActivityCard>
        </div>

        <div className="nsw-col nsw-col-md-6 nsw-col-lg-3">
          <ActivityCard>
            <ActivityHeader
              icon="edit"
              to={(loc) => ({
                pathname: "/my-activity/applications",
                state: loc.state,
              })}
            >
              <span>
                {pluralize(
                  state.data.applicationCount,
                  "Application",
                  "Applications"
                )}
              </span>
            </ActivityHeader>
            {state.data.applicationCount > 0 ? (
              <ActivityList data={state.data.applications} />
            ) : (
              <p className="nsw-card__copy">You have no past applications.</p>
            )}
          </ActivityCard>
        </div>
      </section>

      {state.data.suggestionCount !== 0 ? (
        <section>
          <header className="border-bottom">
            <h2 className="display-4 mb-3 font-weight-bold">
              Suggested opportunities
            </h2>
            <p>See personalised recommendations based on your email alerts.</p>
          </header>

          <ul className="list-unstyled">
            {state.data.suggestions.map((n) => (
              <li key={n.id} className="my-0">
                <SuggestedOpportunity
                  to={{
                    pathname: n.url,
                    state: {
                      referrer: {
                        ...location,
                        state: { ...location.state, children: "My Activity" },
                      },
                    },
                  }}
                  title={n.name}
                  organisation={n.organisationName}
                />
              </li>
            ))}
          </ul>

          {state.data.suggestionCount > 5 && (
            <div className="d-flex align-items-center mb-5">
              <Link
                to={(loc) => ({
                  pathname: "/my-activity/suggestions",
                  state: loc.state,
                })}
              >
                View all
                <i
                  tabIndex={-1}
                  aria-hidden="true"
                  className="material-icons nsw-material-icons ml-1 mb-1 fs-6"
                >
                  east
                </i>
              </Link>
            </div>
          )}
        </section>
      ) : null}
    </>
  );
};
